import { useState, useEffect } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import {
  Send,
  Sparkles,
  Copy,
  Check,
  RefreshCw,
  Trash2,
  Mail,
  Linkedin,
  Twitter,
  Clock,
  User,
  Building2,
  ChevronDown
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EmailComposer from "./EmailComposer";

const OUTREACH_TEMPLATES = {
  introduction: { title: "Introduction Email", description: "Introduce yourself and your services", icon: "👋" },
  follow_up: { title: "Follow-up Email", description: "Follow up after initial contact", icon: "🔄" },
  portfolio_share: { title: "Portfolio Share", description: "Share your work samples", icon: "📁" },
  referral_ask: { title: "Referral Request", description: "Ask for referrals", icon: "🤝" },
  linkedin_connection: { title: "LinkedIn Connection", description: "LinkedIn message", icon: "💼" },
  proposal_pitch: { title: "Proposal Pitch", description: "Pitch with mini-proposal", icon: "📝" },
  reactivation: { title: "Client Reactivation", description: "Re-engage past clients", icon: "🔙" }
};

const PLATFORM_ICONS = {
  email: Mail,
  linkedin: Linkedin,
  twitter: Twitter
};

export default function ColdOutreach() {
  const [outreachList, setOutreachList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("introduction");
  const [platform, setPlatform] = useState("email");
  const [tone, setTone] = useState("professional");
  const [recipient, setRecipient] = useState({ name: "", company: "", role: "" });
  const [context, setContext] = useState({ service_type: "", unique_value: "", portfolio_link: "" });
  const [generatedContent, setGeneratedContent] = useState("");
  const [currentOutreachId, setCurrentOutreachId] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [showForm, setShowForm] = useState(true);

  useEffect(() => {
    fetchOutreach();
  }, []);

  const fetchOutreach = async () => {
    setLoading(true);
    try {
      const res = await api.get("/outreach");
      setOutreachList(res.data.items || []);
    } catch (e) {
      console.error("Error fetching outreach:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!recipient.name && !recipient.company) {
      toast.error("Please enter at least a recipient name or company");
      return;
    }

    setGenerating(true);
    try {
      const res = await api.post("/outreach/generate", {
        template: selectedTemplate,
        platform,
        tone,
        recipient_name: recipient.name,
        recipient_company: recipient.company,
        recipient_role: recipient.role,
        context
      });

      setGeneratedContent(res.data.body);
      setCurrentOutreachId(res.data.outreach_id);
      setShowForm(false);
      toast.success("Outreach message generated!");
      fetchOutreach();
    } catch (e) {
      console.error("Generate error:", e);
      toast.error(e.response?.data?.detail || "Failed to generate outreach");
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(generatedContent);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDelete = async (outreachId) => {
    if (!window.confirm("Delete this outreach message?")) return;

    try {
      await api.delete(`/outreach/${outreachId}`);
      toast.success("Outreach deleted");
      if (currentOutreachId === outreachId) {
        setCurrentOutreachId(null);
        setGeneratedContent("");
        setShowForm(true);
      }
      fetchOutreach();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const handleMarkSent = async (outreachId) => {
    try {
      await api.put(`/outreach/${outreachId}`, { status: "sent" });
      toast.success("Marked as sent!");
      fetchOutreach();
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const loadOutreach = (item) => {
    setGeneratedContent(item.body);
    setCurrentOutreachId(item.id);
    setSelectedTemplate(item.template);
    setPlatform(item.platform);
    setRecipient(item.recipient || {});
    setShowForm(false);
  };

  const resetForm = () => {
    setShowForm(true);
    setGeneratedContent("");
    setCurrentOutreachId(null);
    setRecipient({ name: "", company: "", role: "" });
    setContext({ service_type: "", unique_value: "", portfolio_link: "" });
  };

  const PlatformIcon = PLATFORM_ICONS[platform] || Mail;

  return (
    <div className="space-y-6" data-testid="cold-outreach">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-indigo-500">Cold Outreach</h1>
          <p className="text-indigo-400">Generate personalized outreach messages</p>
        </div>
        <Badge className="bg-indigo-500/20 text-indigo-400">
          {outreachList.length} message{outreachList.length !== 1 ? "s" : ""} saved
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Templates & History */}
        <div className="lg:col-span-1 space-y-4">
          {/* Template Selection */}
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-indigo-500 text-sm">Message Templates</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {Object.entries(OUTREACH_TEMPLATES).map(([key, template]) => (
                <button
                  key={key}
                  onClick={() => { setSelectedTemplate(key); setShowForm(true); }}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${
                    selectedTemplate === key && showForm
                      ? "bg-indigo-500/20 border border-indigo-500/50"
                      : "bg-slate-900/30 hover:bg-slate-900/50"
                  }`}
                  data-testid={`outreach-template-${key}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{template.icon}</span>
                    <div>
                      <p className="text-sm font-medium text-slate-600">{template.title}</p>
                      <p className="text-xs text-slate-400">{template.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>

          {/* Saved Outreach */}
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-indigo-500 text-sm flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Recent Outreach
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-4">
                  <RefreshCw className="w-5 h-5 text-indigo-400 animate-spin" />
                </div>
              ) : outreachList.length > 0 ? (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {outreachList.slice(0, 10).map((item) => (
                    <div
                      key={item.id}
                      className={`flex items-center justify-between p-2 rounded-lg transition-colors ${
                        currentOutreachId === item.id
                          ? "bg-indigo-500/20 border border-indigo-500/50"
                          : "bg-slate-900/30 hover:bg-slate-900/50"
                      }`}
                    >
                      <button
                        onClick={() => loadOutreach(item)}
                        className="flex-1 text-left"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm">{OUTREACH_TEMPLATES[item.template]?.icon || "📧"}</span>
                          <div>
                            <p className="text-xs text-slate-600 truncate">
                              {item.recipient?.name || item.recipient?.company || "Unknown"}
                            </p>
                            <p className="text-xs text-slate-400">{item.platform}</p>
                          </div>
                        </div>
                      </button>
                      <div className="flex items-center gap-1">
                        <Badge className={`text-xs ${
                          item.status === "sent" ? "bg-green-500/20 text-green-400" : "bg-slate-400 text-indigo-400"
                        }`}>
                          {item.status}
                        </Badge>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-1 text-slate-400 hover:text-red-400"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-4">No outreach saved yet</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right: Generator / Preview */}
        <div className="lg:col-span-2">
          {showForm ? (
            <Card className="bg-slate-800/50 border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-indigo-500 flex items-center gap-2">
                  <Send className="w-5 h-5 text-indigo-400" />
                  Compose Outreach
                </CardTitle>
                <CardDescription className="text-indigo-400">
                  {OUTREACH_TEMPLATES[selectedTemplate]?.title}: {OUTREACH_TEMPLATES[selectedTemplate]?.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Platform & Tone */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Platform</label>
                    <Select value={platform} onValueChange={setPlatform}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="email" className="text-slate-400">📧 Email</SelectItem>
                        <SelectItem value="linkedin" className="text-slate-400">💼 LinkedIn</SelectItem>
                        <SelectItem value="twitter" className="text-slate-400">🐦 Twitter/DM</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Tone</label>
                    <Select value={tone} onValueChange={setTone}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="professional" className="text-slate-400">Professional</SelectItem>
                        <SelectItem value="friendly" className="text-slate-400">Friendly</SelectItem>
                        <SelectItem value="casual" className="text-slate-400">Casual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Recipient Info */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-slate-400 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Recipient Details
                  </h4>
                  <div className="grid grid-cols-3 gap-3">
                    <input
                      type="text"
                      placeholder="Name"
                      value={recipient.name}
                      onChange={(e) => setRecipient({ ...recipient, name: e.target.value })}
                      className="px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 focus:border-indigo-500 focus:outline-none"
                      data-testid="recipient-name"
                    />
                    <input
                      type="text"
                      placeholder="Company"
                      value={recipient.company}
                      onChange={(e) => setRecipient({ ...recipient, company: e.target.value })}
                      className="px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 focus:border-indigo-500 focus:outline-none"
                      data-testid="recipient-company"
                    />
                    <input
                      type="text"
                      placeholder="Role/Title"
                      value={recipient.role}
                      onChange={(e) => setRecipient({ ...recipient, role: e.target.value })}
                      className="px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                </div>

                {/* Your Context */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-slate-400 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Your Context
                  </h4>
                  <input
                    type="text"
                    placeholder="Your services (e.g., Web Design, Brand Strategy)"
                    value={context.service_type}
                    onChange={(e) => setContext({ ...context, service_type: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 focus:border-indigo-500 focus:outline-none"
                    data-testid="service-type"
                  />
                  <Textarea
                    placeholder="Your unique value proposition (what makes you different?)"
                    value={context.unique_value}
                    onChange={(e) => setContext({ ...context, unique_value: e.target.value })}
                    className="min-h-[60px] bg-slate-900/50 border-slate-600 text-slate-600"
                  />
                  <input
                    type="text"
                    placeholder="Portfolio link (optional)"
                    value={context.portfolio_link}
                    onChange={(e) => setContext({ ...context, portfolio_link: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 focus:border-indigo-500 focus:outline-none"
                  />
                </div>

                {/* Generate Button */}
                <button
                  onClick={handleGenerate}
                  disabled={generating}
                  className="w-full flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-600 disabled:bg-slate-600 text-white px-4 py-3 rounded-xl transition-colors"
                  data-testid="generate-outreach-btn"
                >
                  {generating ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Generate Outreach
                    </>
                  )}
                </button>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-slate-800/50 border-slate-700/50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <PlatformIcon className="w-5 h-5 text-indigo-400" />
                    <CardTitle className="text-indigo-500">
                      {OUTREACH_TEMPLATES[selectedTemplate]?.title}
                    </CardTitle>
                    <Badge className="bg-slate-400 text-slate-400">{platform}</Badge>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={resetForm}
                      className="px-3 py-1.5 text-sm text-indigo-400 hover:text-slate-600 transition-colors"
                    >
                      New Message
                    </button>
                    <button
                      onClick={handleCopy}
                      className="p-2 bg-slate-400 hover:bg-slate-600 rounded-lg transition-colors"
                    >
                      {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-indigo-400" />}
                    </button>
                    {platform === "email" && (
                      <button
                        onClick={() => setShowEmailComposer(true)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-indigo-500 rounded-lg transition-colors text-sm"
                        data-testid="send-outreach-email-btn"
                      >
                        <Send className="w-4 h-4" />
                        Send
                      </button>
                    )}
                    {currentOutreachId && (
                      <button
                        onClick={() => handleMarkSent(currentOutreachId)}
                        className="px-3 py-1.5 bg-blue-500/20 hover:bg-slate-400/30 text-indigo-400 rounded-lg transition-colors text-sm"
                      >
                        Mark Sent
                      </button>
                    )}
                  </div>
                </div>
                {recipient.name && (
                  <p className="text-sm text-indigo-400 mt-2">
                    To: {recipient.name} {recipient.company && `at ${recipient.company}`}
                  </p>
                )}
              </CardHeader>
              <CardContent>
                <Textarea
                  value={generatedContent}
                  onChange={(e) => setGeneratedContent(e.target.value)}
                  className="min-h-[400px] font-mono text-sm bg-slate-900/50 border-slate-600 text-slate-600"
                  data-testid="outreach-editor"
                />
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Email Composer Modal */}
      <EmailComposer
        isOpen={showEmailComposer}
        onClose={() => setShowEmailComposer(false)}
        initialContent={generatedContent}
        clients={[]}
      />
    </div>
  );
}
